import { ModalOptimaNumberVouchers } from "@components/Modals/ModalOptimaNumberVouchers";
import { useEffect, useState } from "react";
import Chart from "react-apexcharts";

const ClientsChart = ({ clientdata, filterStartDate = null, filterEndDate = null }) => {
	const [filteredData, setFilteredData] = useState([]);
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedDay, setSelectedDay] = useState(null);
	const [selectedVouchers, setSelectedVouchers] = useState([]);

	useEffect(() => {
		if (!Array.isArray(clientdata)) {
			console.error("clientdata is either undefined or not an array.");
			return;
		}

		const getLast7Days = () => {
			const today = new Date();
			const last7Days = [];
			for (let i = 6; i >= 0; i--) {
				const day = new Date(today);
				day.setDate(today.getDate() - i);
				last7Days.push(day.toISOString().split("T")[0]);
			}
			return last7Days;
		};

		const last7Days = getLast7Days();

		const startDate = filterStartDate
			? new Date(filterStartDate).toISOString().split("T")[0]
			: null;

		const endDate = filterEndDate
			? new Date(filterEndDate).toISOString().split("T")[0]
			: new Date().toISOString().split("T")[0];

		const flatData = clientdata.flatMap((item) => item);

		let filtered = [];

		const normalize = (d) => new Date(d).toISOString().split("T")[0];

		if (startDate && endDate) {
			const dataMap = new Map(flatData.map((item) => [item.day, item]));

			filtered = generateDateRange(new Date(startDate), new Date(endDate)).map((date) => {
				const formattedDate = normalize(date);
				const dataForDate = dataMap.get(formattedDate);

				return {
					day: formattedDate,
					usages: dataForDate?.usages || 0,
					online_vouchers: dataForDate?.online_vouchers || [],
				};
			});
		} else if (startDate && !endDate) {
			filtered = flatData
				.filter((data) => data.day >= startDate)
				.map((data) => ({
					day: data.day,
					usages: data.usages || 0,
					online_vouchers: data.online_vouchers || [],
				}));
		} else if (!startDate && endDate) {
			filtered = flatData
				.filter((data) => data.day <= endDate)
				.map((data) => ({
					day: data.day,
					usages: data.usages || 0,
					online_vouchers: data.online_vouchers || [],
				}));
		} else {
			filtered = last7Days.map((date) => {
				const dataForDate = flatData.find((d) => d.day === date);
				return {
					day: date,
					usages: dataForDate ? dataForDate.usages : 0,
					online_vouchers: dataForDate?.online_vouchers || [],
				};
			});
		}

		setFilteredData(filtered);
	}, [clientdata, filterStartDate, filterEndDate]);

	const generateDateRange = (start, end) => {
		const dates = [];
		let currentDate = new Date(start);
		while (currentDate <= end) {
			dates.push(currentDate.toISOString().split("T")[0]);
			currentDate.setDate(currentDate.getDate() + 1);
		}
		return dates;
	};

	const data = filteredData.map((entry) => ({
		x: entry.day,
		y: entry.usages,
	}));

	const handleBarClick = (event, chartContext, config) => {
		const index = config.dataPointIndex;
		if (index === -1 || !filteredData[index]) return;

		const clicked = filteredData[index];

		setSelectedDay(clicked.day);
		setSelectedVouchers(clicked.online_vouchers);
		setModalOpen(true);
	};

	const chartOptions = {
		chart: {
			type: "bar",
			height: "350px",
			fontFamily: "Inter, sans-serif",
			toolbar: { show: false },
			animations: { enabled: true, easing: "easeinout", speed: 800 },
			events: {
				dataPointSelection: handleBarClick,
			},
		},
		tooltip: {
			shared: true,
			intersect: false,
			style: {
				fontFamily: "Inter, sans-serif",
				cssClass: "text-xs font-normal fill-gray-500 fill-gray-400",
			},
			theme: "dark",
		},
		states: {
			hover: { filter: { type: "darken", value: 1 } },
		},
		stroke: { show: true, width: 0, colors: ["transparent"] },
		grid: { show: true, strokeDashArray: 4, padding: { left: 2, right: 2, top: -14 } },
		dataLabels: { enabled: false },
		legend: {
			show: true,
			showForSingleSeries: true,
			position: "top",
			horizontalAlign: "right",
			onItemClick: { toggleDataSeries: false },
			onItemHover: { highlightDataSeries: false },
		},
		xaxis: {
			categories: data.map((point) => point.x),
			labels: {
				rotate: -45,
				style: {
					fontFamily: "Inter, sans-serif",
					fontSize: "12px",
					fontWeight: "bold",
					colors: "#FFFFFF",
				},
			},
			axisBorder: { show: true, color: "#FFFFFF" },
			axisTicks: { show: true, color: "#FFFFFF" },
		},
		yaxis: {
			labels: {
				formatter: (value) => Math.round(value),
				style: {
					fontFamily: "Inter, sans-serif",
					fontSize: "12px",
					fontWeight: "bold",
					colors: "#FFFFFF",
				},
			},
			title: {
				style: {
					fontFamily: "Inter, sans-serif",
					fontSize: "12px",
					fontWeight: "bold",
					color: "#FFFFFF",
				},
			},
			axisBorder: { show: true, color: "#FFFFFF" },
			axisTicks: { show: true, color: "#FFFFFF" },
		},
		fill: { opacity: 1 },
		colors: ["#00C0FF"],
		plotOptions: {
			bar: {
				horizontal: false,
				columnWidth: "45%",
				borderRadiusApplication: "end",
				borderRadius: 6,
				cursor: "pointer",
			},
		},
	};

	const series = [
		{
			name: "Vouchers with Usage",
			color: "#727DA3",
			data: data,
		},
	];

	return (
		<>
			<Chart options={chartOptions} series={series} type="bar" height="350px" />
			<ModalOptimaNumberVouchers
				isOpen={modalOpen}
				closeModal={() => setModalOpen(false)}
				day={selectedDay}
				online_vouchers={selectedVouchers}
			/>
		</>
	);
};

export default ClientsChart;
